import { v4 as uuid } from 'uuid';
import { TranslatableField } from '../../TranslatableField';
import { ContentBlockType } from '../ContentBlockType';
import {
  ContentBlockDataModel,
  ContentBlockImageData,
  ContentBlockImageSize,
  ContentBlockListItemModel,
  ContentBlockModel,
  ContentBlockSettingsModel,
  ContentBlockTextVerticalAlignment,
  createContentBlockImageData,
} from '../ContentBlockModel';

export enum SpeakersBlockLayout {
  List = 'list',
  Column = 'column',
  Card = 'card',
}

export interface SpeakersBlockDataModel extends ContentBlockDataModel {
  htmlContent: TranslatableField;
  items: SpeakersBlockListItemModel[];
}

export interface SpeakersBlockSettingsModel extends ContentBlockSettingsModel {
  withIntro: boolean;
  withImage: boolean;
  withAffiliation: boolean;
  withBio: boolean;
  shouldOpenBioInModal: boolean;
  imageSize: ContentBlockImageSize;
  textVerticalAlignment: ContentBlockTextVerticalAlignment;
  layout: SpeakersBlockLayout;
  maxItemsInRow: number;
}

export interface SpeakersBlockModel extends ContentBlockModel<SpeakersBlockDataModel, SpeakersBlockSettingsModel> {
  type: ContentBlockType.Speakers;
}

export function createSpeakersBlock(
  order: number = 0,
  layout: SpeakersBlockLayout = SpeakersBlockLayout.List,
): SpeakersBlockModel {
  return {
    id: uuid(),
    type: ContentBlockType.Speakers,
    order,
    newData: {
      htmlContent: {
        fr: `
            <h2>Nos intervenants</h2>
            <p>Ceci est un texte de remplacement pour la section intervenants. Utilisez cet espace pour présenter les conférenciers, avec leurs noms, affiliations et courtes biographies.</p>
          `,
        en: `
            <h2>Meet the Speakers</h2>
            <p>This is placeholder text for your speaker section. Use this space to introduce speakers or panelists, including their names, affiliations, and bios.</p>
          `,
      },
      items: [createSpeakersBlockListItem(1), createSpeakersBlockListItem(2), createSpeakersBlockListItem(3)],
    },
    settings: {
      withIntro: true,
      withImage: true,
      withAffiliation: true,
      withBio: true,
      shouldOpenBioInModal: false,
      imageSize: ContentBlockImageSize.Medium,
      textVerticalAlignment: ContentBlockTextVerticalAlignment.Center,
      layout,
      maxItemsInRow: 3,
    },
  };
}

export interface SpeakersBlockListItemModel extends ContentBlockListItemModel {
  name: TranslatableField;
  affiliation: TranslatableField;
  htmlBio: TranslatableField;
  image: ContentBlockImageData;
}

export function createSpeakersBlockListItem(number?: number): SpeakersBlockListItemModel {
  return {
    id: uuid(),
    name: {
      fr: `Nom Intervenant${number ? ` ${number.toString()}` : ''}`,
      en: `Speaker Name${number ? ` ${number.toString()}` : ''}`,
    },
    affiliation: { fr: 'Organisation ou institution', en: 'Organization or Institution' },
    htmlBio: {
      fr: '<p>Ceci est une bio factice. Ajoutez des détails pertinents comme l’expertise, les thèmes abordés ou l’expérience de prise de parole.</p>',
      en: '<p>This is a placeholder bio. Add relevant details like expertise, speaking experience, or session topics.</p>',
    },
    image: createContentBlockImageData(),
  };
}

export const speakerLayoutImagesMap: Record<
  SpeakersBlockLayout,
  {
    url: string;
    width: number;
    height: number;
  }
> = {
  [SpeakersBlockLayout.Card]: {
    url: '/images/content_blocks/speakers_card',
    width: 1564,
    height: 786,
  },
  [SpeakersBlockLayout.Column]: {
    url: '/images/content_blocks/speakers_column',
    width: 1564,
    height: 678,
  },
  [SpeakersBlockLayout.List]: {
    url: '/images/content_blocks/speakers_list',
    width: 1564,
    height: 734,
  },
};

export const speakerLayoutModifiersMap: Record<SpeakersBlockLayout, string> = {
  [SpeakersBlockLayout.Card]: '-card',
  [SpeakersBlockLayout.Column]: '-column',
  [SpeakersBlockLayout.List]: '-list',
};
